/*    
<summary>
  This functional component "AddStaffManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:01-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { registrationTypeOptions } from "../../../../constants/options.constant";
import { useStore } from "../../../../contexts/store-provider";
import { IAddProductManagement } from "../../../../models/forms/IAddProduct";
import { formatMessage } from "../../../../translations/format-message";
import StoreAddProductManagementForm from "../forms/add-edit-store-product-form";

interface IProps {
  id: number;
  modalClosed: () => void;
  storeId: number;
}

const StoreAddEditProductManagement = (props: IProps) => {
  const {
    productManagementStore,
    masterManagementStore,
    categoryManagementStore,
  } = useStore();
  const {
    GetProductManagementByIdService,
    resetAddUpdateProductManagementState,
    resetProductManagementDetail,
    productState,
    productDetail,
    addUpdateProductManagementState,
    AddProductManagementService,
    UpdateProductManagementService,
    deleteProductManagementState,
    DeleteProductManagementService,
    resetDeleteProductManagement,
    getProductData, // Added to preserve this data
  } = productManagementStore;
  const {
    resetCategoryList,
    allCategoryManagementStoreForAddProduct,
    GetCategoryManagementListFilterService,
    inProgress,
    GetCategorySummaryListByStoreIdProductService,
  } = categoryManagementStore;
  const {
    getAllTaxOptions,
    GetAllTaxOptionService,
    getAllBusinessTypes,
    GetAllBusinessTypeService,
  } = masterManagementStore;
  const initialValues: IAddProductManagement = productDetail;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    // Store the productData before we make any API calls that might change it
    const originalProductData = new Map(getProductData);

    GetAllBusinessTypeService();
    GetAllTaxOptionService();
    if (props.storeId > 0) {
      GetCategorySummaryListByStoreIdProductService(2, props.storeId);
    }
    if (props.id > 0) {
      GetProductManagementByIdService(props.id, props.storeId);
    }

    // Return cleanup function to preserve product data
    return () => {
      // This is important: when the component unmounts, don't reset the product data
      // Only reset the form-specific states to allow parent to handle the refresh
      resetAddUpdateProductManagementState();

      // Instead of calling resetProductManagementDetail which might clear all product data,
      // we'll handle the cleanup more specifically
      if (productManagementStore.productState) {
        productManagementStore.productState = {
          success: false,
          error: "",
          inProgress: false,
        };
        productManagementStore.product = undefined;
      }
    };
  }, [
    GetAllBusinessTypeService,
    GetAllTaxOptionService,
    GetCategorySummaryListByStoreIdProductService,
    GetProductManagementByIdService,
    props.id,
    props.storeId,
    resetAddUpdateProductManagementState,
    productManagementStore,
    getProductData,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateProductManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateProductManagementState.success) {
      // Don't immediately reset the success state - let the parent component handle the refresh first
      // The parent will detect this success state and refresh the products

      // Show toast message
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));

      // Close the modal - the parent will handle refreshing data
      props.modalClosed();

      // Note: We're not resetting success state here to allow parent to detect and refresh
    }
  }, [addUpdateProductManagementState.success, props]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "productDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (productState.error) {
      toast.error(formatMessage(productState.error));
    }
  }, [productState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateProductManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateProductManagementState.error) {
      toast.error(formatMessage(addUpdateProductManagementState.error));
      resetAddUpdateProductManagementState();
    }
  }, [
    addUpdateProductManagementState.error,
    resetAddUpdateProductManagementState,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteProductManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteProductManagementState.success) {
      // Similar to add/update, we don't reset the success state here
      // Let the parent component detect it and refresh the products

      toast.success(formatMessage("deleted_success"));

      // Close the modal - the parent will handle refreshing data
      props.modalClosed();

      // Note: We're not resetting success state here to allow parent to detect and refresh
    }
  }, [deleteProductManagementState.success, props]);

  useEffect(() => {
    if (deleteProductManagementState.error) {
      toast.error(formatMessage(deleteProductManagementState.error));
      resetDeleteProductManagement();
      props.modalClosed(); 
    }
  }, [deleteProductManagementState.error, resetDeleteProductManagement]);

  /**
   * This function adds the product when "Id < 0" and updates the Existing product when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IAddProductManagement) => {
    let tempValues = cloneDeep(values);
    tempValues.ProductName = values.ProductName.trim();
    if (props.id < 0) AddProductManagementService(tempValues);
    else {
      if(initialValues.Status == 1 && tempValues.Status == 1)
        tempValues.IsStatusChanged = true;
      tempValues.IsStatusChanged = false;
      UpdateProductManagementService(props.id, {
        ...tempValues,
        IsBarcodeChanged: initialValues?.BarCode !== tempValues.BarCode,
        StoreId: initialValues.StoreId ? initialValues.StoreId : null
      });
    }
  };

  /**
   * This function closes the pop up modal only
   * We don't reset success states here to allow the parent component to detect them
   */
  const onModalClosed = () => {
    // Don't reset anything here, just close the modal
    props.modalClosed();
  };

  const handleDeleteSubmitHandler = () => {
    DeleteProductManagementService(props.id);
    // Don't reset the delete success state - let the parent component handle refreshing first
  };

  /**
   * This function closes the pop up modal only
   * We don't reset success states here to allow the parent component to detect them
   */
  const onDeleteModalClosed = () => {
    // Don't reset anything here, just close the modal
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <StoreAddProductManagementForm
        id={props.id}
        initialValues={initialValues}
        getProductManagementLoading={
          productState.inProgress ||
          inProgress ||
          addUpdateProductManagementState.inProgress
        }
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        businessTypeOptions={getAllBusinessTypes}
        categoryOptions={allCategoryManagementStoreForAddProduct}
        taxOptions={getAllTaxOptions}
        registrationTypeOptions={registrationTypeOptions}
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        deleteActionLoading={deleteProductManagementState.inProgress}
      />
    </React.Fragment>
  );
};

export default observer(StoreAddEditProductManagement);
